import React from 'react';
import FinanceInteractive from './FinanceInteractive';
import ArtsHumanitiesInteractive from './ArtsHumanitiesInteractive';
import SocialSciencesInteractive from './SocialSciencesInteractive';
import NaturalSciencesInteractive from './NaturalSciencesInteractive';
import EngineeringTechInteractive from './EngineeringTechInteractive';
import HealthMedicalInteractive from './HealthMedicalInteractive';
import MathInteractive from './MathInteractive';
import LawInteractive from './LawInteractive';
import EducationInteractive from './EducationInteractive';
import CommunicationInteractive from './CommunicationInteractive';
import InteractiveExercise from './InteractiveExercise';

// Import placeholders for remaining subjects
import { 
  PaintBrushIcon
} from '@heroicons/react/24/outline';

interface SubjectInteractiveRouterProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      subjectType?: string;
      exerciseType: string;
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
      template?: any;
      initialCode?: string;
      expectedOutput?: string;
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

// Placeholder component for subjects not yet implemented
const PlaceholderInteractive: React.FC<{
  content: any;
  onComplete: (completed: boolean, score?: number) => void;
  subjectName: string;
  icon: React.ReactNode;
  color: string;
}> = ({ content, onComplete, subjectName, icon, color }) => {
  const handleComplete = () => {
    // Auto-pass placeholder for now
    onComplete(true, 85);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className={`bg-gradient-to-r ${color} text-white p-6`}>
        <div className="flex items-center">
          {icon}
          <div className="ml-3">
            <h3 className="text-xl font-bold">{content.title}</h3>
            <p className="text-white text-opacity-90 text-sm">{content.description}</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <div className="text-center py-12">
          <div className="mb-4">
            {icon}
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {subjectName} Interactive Coming Soon!
          </h4>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            We're working on comprehensive interactive exercises for {subjectName}. 
            This subject-specific component will include specialized tools and assessments.
          </p>
          <div className="space-y-2 text-sm text-gray-500 mb-6">
            <p><strong>Exercise Type:</strong> {content.interactiveData?.exerciseType || 'Multiple Types'}</p>
            <p><strong>Subject Area:</strong> {subjectName}</p>
          </div>
          
          <button
            onClick={handleComplete}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            Continue (Temporary Pass)
          </button>
        </div>
      </div>
    </div>
  );
};

const SubjectInteractiveRouter: React.FC<SubjectInteractiveRouterProps> = ({
  content,
  onComplete
}) => {
  const subjectType = content.interactiveData?.subjectType?.toLowerCase() || 
                     content.type?.toLowerCase() || 
                     'general';

  function getComponentName(type: string): string {
    if (['finance', 'finance-interactive'].includes(type)) return 'FinanceInteractive';
    if (['arts', 'humanities', 'arts-humanities', 'arts-interactive', 'humanities-interactive', 'design', 'art', 'visual-arts', 'graphic-design', 'design-interactive'].includes(type)) return 'ArtsHumanitiesInteractive';
    if (['social-sciences', 'social-sciences-interactive', 'social', 'sociology', 'psychology', 'political-science', 'social-interactive', 'social-science', 'psych', 'politics', 'economics', 'anthropology'].includes(type)) return 'SocialSciencesInteractive';
    if (['natural-sciences', 'natural-sciences-interactive', 'science', 'biology', 'chemistry', 'physics', 'science-interactive', 'natural-science', 'bio', 'chem', 'phys'].includes(type)) return 'NaturalSciencesInteractive';
    if (['engineering', 'engineering-tech-interactive', 'technology', 'computer-science', 'engineering-tech', 'tech-interactive', 'engineering-interactive', 'tech', 'computer', 'software', 'hardware'].includes(type)) return 'EngineeringTechInteractive';
    if (['health', 'medical', 'medicine', 'nursing', 'health-interactive', 'medical-interactive'].includes(type)) return 'HealthMedicalInteractive';
    if (['law', 'legal', 'jurisprudence', 'law-interactive', 'legal-interactive'].includes(type)) return 'LawInteractive';
    if (['education', 'pedagogy', 'teaching', 'education-interactive'].includes(type)) return 'EducationInteractive';
    if (['communication', 'media', 'journalism', 'communications', 'communication-interactive'].includes(type)) return 'CommunicationInteractive';
    if (['math', 'math-interactive', 'mathematics', 'statistics', 'calculus'].includes(type)) return 'MathInteractive';
    return 'ArtsHumanitiesInteractive (fallback)';
  }

  // Route to appropriate interactive component based on subject type
  switch (subjectType) {
    case 'coding':
    case 'coding-interactive':
    case 'programming':
      const codingData = {
        instructions: content.interactiveData?.instructions || content.description || 'Complete the coding exercise',
        initialCode: content.interactiveData?.initialCode || '// Write your code here\n// Complete the exercise below\n\nfunction yourFunction() {\n    // Implement your solution\n    \n}',
        expectedOutput: content.interactiveData?.expectedOutput || 'Your solution should work correctly',
        hints: content.interactiveData?.hints || ['Read the instructions carefully', 'Test your code step by step', 'Check for syntax errors']
      };
      
      return (
        <InteractiveExercise 
          exerciseData={codingData}
          onInteraction={(interaction) => console.log('Coding interaction:', interaction)}
          onGradingUpdate={(grading) => {
            onComplete(grading.score >= 70, grading.score);
          }}
        />
      );
      
    case 'finance':
    case 'finance-interactive':
      return (
        <FinanceInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'arts':
    case 'humanities':
    case 'arts-humanities':
    case 'arts-interactive':
    case 'humanities-interactive':
      return (
        <ArtsHumanitiesInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'social-sciences':
    case 'social-sciences-interactive':
    case 'social':
    case 'sociology':
    case 'psychology':
    case 'political-science':
    case 'social-interactive':
    case 'social-science':
    case 'psych':
    case 'politics':
    case 'economics':
    case 'anthropology':
      return (
        <SocialSciencesInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'natural-sciences':
    case 'natural-sciences-interactive':
    case 'science':
    case 'biology':
    case 'chemistry':
    case 'physics':
    case 'science-interactive':
    case 'natural-science':
    case 'bio':
    case 'chem':
    case 'phys':
      return (
        <NaturalSciencesInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'engineering':
    case 'engineering-tech-interactive':
    case 'technology':
    case 'computer-science':
    case 'engineering-tech':
    case 'tech-interactive':
    case 'engineering-interactive':
    case 'tech':
    case 'computer':
    case 'software':
    case 'hardware':
      return (
        <EngineeringTechInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'health':
    case 'medical':
    case 'medicine':
    case 'nursing':
    case 'health-interactive':
    case 'medical-interactive':
      return (
        <HealthMedicalInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'law':
    case 'legal':
    case 'jurisprudence':
    case 'law-interactive':
    case 'legal-interactive':
      return (
        <LawInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'education':
    case 'pedagogy':
    case 'teaching':
    case 'education-interactive':
      return (
        <EducationInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'design':
    case 'art':
    case 'visual-arts':
    case 'graphic-design':
    case 'design-interactive':
      return (
        <ArtsHumanitiesInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'communication':
    case 'media':
    case 'journalism':
    case 'communications':
    case 'communication-interactive':
      return (
        <CommunicationInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    case 'math':
    case 'mathematics':
    case 'statistics':
    case 'calculus':
    case 'math-interactive':
      return (
        <MathInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );

    default:
      // Log unknown type and provide better fallback
      console.warn(`Unknown subject type "${subjectType}" - falling back to ArtsHumanitiesInteractive`);
      console.log('Available types:', [
        'finance', 'arts', 'humanities', 'social-sciences', 'social', 
        'natural-sciences', 'science', 'engineering', 'technology', 
        'health', 'medical', 'law', 'education', 'communication', 'math'
      ]);
      
      // Route to ArtsHumanitiesInteractive as a general fallback
      return (
        <ArtsHumanitiesInteractive 
          content={content as any} 
          onComplete={onComplete} 
        />
      );
  }
};

export default SubjectInteractiveRouter;